import { useMutationTranslateDocument } from "@apis:translation/document";
import { Header } from "@components:Header";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { BotSelectionPersistent } from "@modules:BotSelection/BotSelection";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { UploadButton } from "@pages:app/docs/Components/UploadButton";
import { useToasterStoreActions } from "@stores:global";
import FileSaver from "file-saver";
import { ChangeEvent, useState } from "react";
import { SiGoogletranslate } from "react-icons/si";

import { AppType } from "@:constants";

const SUPPORTED_LANGUAGES = [
  "English",
  "French",
  "German",
  "Italian",
  "Spanish",
  "Portuguese",
  "Arabic",
];

export const DocumentTranslation = () => {
  const [file, setFile] = useState<File | null>(null);
  const [targetLanguage, setTargetLanguage] = useState<string>();
  const [translatedBlob, setTranslatedBlob] = useState<Blob | null>(null);

  const { getSelectedBot } = useBotSelection();
  const toastActions = useToasterStoreActions();
  const bot = getSelectedBot();
  const botId = bot?.id || "";

  const mutation = useMutationTranslateDocument(botId, {
    onSuccess: (blob) => setTranslatedBlob(blob),
    onError: (error) => {
      toastActions.setToaster({
        variant: "error",
        message: `Translation failed: ${error.message}`,
        open: true,
      });
    },
  });

  const handleSubmit = () => {
    if (!file || !botId || !targetLanguage) return;
    mutation.mutate({ file, targetLanguage });
  };
  const handleDownload = () => {
    if (!translatedBlob) return;
    const fileName = file?.name
      ? `translated-${file.name}`
      : "translated-document.docx";
    FileSaver.saveAs(translatedBlob, fileName);
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
          flexGrow: 1,
        }}
      >
        <Header
          sx={{
            bgcolor: (theme) => theme.palette.common.white,
            borderTopLeftRadius: 30,
            borderTopRightRadius: 30,
          }}
          leftSide={
            <Box sx={{ ml: 3 }}>
              <SVGInternalIcon
                icon="nemoIcon"
                sx={{ width: 120, height: 120 }}
              />
            </Box>
          }
          rightSide={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ px: 1 }}>
                <BotSelectionPersistent app={AppType.Translation} />
              </Box>
              <SwitchPage />
            </Box>
          }
        />

        <Box sx={{ flex: 1, textAlign: "center", pt: "8%", px: 2 }}>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "bold",
              fontSize: "32px",
              lineHeight: "32px",
              textAlign: "center",
            }}
          >
            Nemo Document Translation
          </Typography>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              marginTop: "10px",
              fontSize: "20px",
              lineHeight: "32px",
              fontWeight: "500",
              textAlign: "center",
            }}
          >
            Upload a <strong>.docx</strong> or <strong>.pptx</strong> document
            to translate it using AI
          </Typography>

          <Box
            sx={{
              width: "300px",
              mx: "auto",
              mt: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <UploadButton
              file={file ?? undefined}
              onFileChange={(e: ChangeEvent<HTMLInputElement>) => {
                setFile(e.target.files?.[0] ?? null);
                setTranslatedBlob(null);
              }}
              fileLabel="DOCX or PPTX file"
              accept=".docx,.pptx"
              sx={{
                width: "300px",
              }}
            />

            <Box sx={{ width: "100%", textAlign: "center" }}>
              <SuperInput.Select
                label="Target Language"
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value as string)}
                fullWidth
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </SuperInput.Select>
            </Box>
          </Box>

          <Box sx={{ mt: 5 }}>
            <Button
              variant="contained"
              disabled={
                !file || !botId || mutation.isPending || !targetLanguage
              }
              onClick={handleSubmit}
              startIcon={
                mutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SiGoogletranslate />
                )
              }
            >
              {mutation.isPending ? "Translating..." : "Translate Document"}
            </Button>
          </Box>

          {translatedBlob && (
            <Box mt={6} textAlign="center">
              <Typography variant="subtitle1" mb={2}>
                Translation complete. You can now download the file.
              </Typography>
              <Button
                startIcon={
                  <SVGInternalIcon
                    icon="download"
                    sx={{ color: "blue", fontSize: "small" }}
                  />
                }
                onClick={handleDownload}
              >
                Download Translated File
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
